import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass, withCtx as _withCtx, normalizeStyle as _normalizeStyle, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import {
  onMounted,
  onUnmounted,
  ref,
  withDefaults,
  computed
} from "vue";
import {
  IonHeader,
  IonPage,
  IonContent,
  IonFooter,
  IonBackdrop,
  isPlatform,
  IonSpinner
} from "@ionic/vue";
import {
  Query,
  RoleEnum,
  UserDocument,
  Facility,
  Trainer
} from "@/generated/graphql";
import { useQuery } from "@vue/apollo-composable";
import NavigationMenu from "@/general/components/NavigationMenu.vue";
import useRoles from "@/hooks/useRole";
import { navigationMenu as navigation } from "@/const/navigation";
import { IonContentCustomEvent, ScrollDetail } from "@ionic/core";
import { EntitiesEnum } from "@/const/entities";
import useId from "@/hooks/useId";
import { useFacilityStore } from "@/general/stores/useFacilityStore";
import { useUserStore } from "@/general/stores/user";
import { useTrainerStore } from "@/general/stores/useTrainerStore";
import { Capacitor } from "@capacitor/core";


const __default__ = {
  name: "BaseLayout",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    hideNavigationMenu: { type: Boolean, default: false },
    headerFixed: { type: Boolean, default: false },
    contentFullHeight: { type: Boolean, default: false },
    fullWidthFooter: { type: Boolean, default: true },
    initialBreakpoint: { default: 0.5 },
    draggable: { type: Boolean, default: false },
    offsetTop: { default: 42 },
    draggableBackground: { default: "" },
    addContentClass: { default: "" }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;



const content = ref<typeof IonContent | null>(null);
const { role } = useRoles();
const { id } = useId();
const facilityStore = useFacilityStore();
const userStore = useUserStore();
const trainerStore = useTrainerStore();

const menuType =
  role === RoleEnum.OrganizationOwner ||
  role === RoleEnum.FacilityOwner ||
  role === RoleEnum.Manager
    ? EntitiesEnum.Facility
    : role;
const menu = navigation[menuType];

const {
  result,
  loading: loadingUser,
  onResult: gotUser,
} = useQuery<Pick<Query, "user">>(UserDocument, { id });
const progress = ref<string | number>("");

const facilities = computed(() => result.value?.user?.owned_facilities);

gotUser(() => {
  switch(role) {
    case RoleEnum.OrganizationOwner :
    case RoleEnum.FacilityOwner :
      if(!localStorage.getItem("selected_facility")) {
        facilityStore.setFacility(facilities.value?.at(0) as Facility);
        localStorage.setItem("selected_facility", facilities.value?.at(0)?.id as string);
      } else {
        let activeId = localStorage.getItem("selected_facility");
        let facilityValue = facilities.value?.find((facility) => facility?.id === activeId);
        if(!facilityValue && facilities.value?.length){
          facilityStore.setFacility(facilities.value?.at(0) as Facility);
          localStorage.setItem("selected_facility", facilities.value?.at(0)?.id as string);
        } else {
          facilityStore.setFacility(facilityValue as Facility);
        }
      }
      break;

    case RoleEnum.Trainer :
      let trainer = {
        id: result?.value?.user?.id,
        first_name: result?.value?.user?.first_name,
        last_name: result?.value?.user?.last_name,
        avatarUrl: result?.value?.user?.avatarUrl,
        tax_id: result?.value?.user?.tax_id,
        email: result?.value?.user?.email,
        address: result?.value?.user?.address,
        currentSubscription: result?.value?.user?.currentSubscription,
        score: result?.value?.user?.score,
        reviews_count: result?.value?.user?.reviews_count,
        recommended_count: result?.value?.user?.recommended_count,
        not_recommended_count: result?.value?.user?.not_recommended_count,
        trainings_count: result?.value?.user?.trainings_count,
        positive_reviews_count: result?.value?.user?.positive_reviews_count,
        negative_reviews_count: result?.value?.user?.negative_reviews_count,
        completed_trainings_count: result?.value?.user?.completed_trainings_count,
        trainer_type: result?.value?.user?.trainer_type,
        trainerRates: result?.value?.user?.trainerRates,
        created_at: result?.value?.user?.created_at,
      };
      trainerStore.setTrainer(trainer as Trainer);
      break;

    default :
      break;
  }

  userStore.setName(result.value?.user?.first_name, result.value?.user?.last_name);
  userStore.setEmail(result.value?.user?.email);
  userStore.setId(result.value?.user?.id);
  userStore.setSubscription(result.value?.user?.currentSubscription);
  userStore.setAvatarUrl(result.value?.user?.avatarUrl);
  userStore.setAddress(result.value?.user?.address?.city?.state, result.value?.user?.address?.city, result.value?.user?.address);
  userStore.setOwnedFacilities(result.value?.user?.owned_facilities);
});
const scrollToBottom = () => {
  if (content.value) {
    content.value.$el.scrollToBottom(500);
  }
};

const showDraggable = () => {
  content?.value?.$el.scrollToPoint(
    0,
    breakpoint.value - props.offsetTop - safeArea.top,
    draggableOptions.duration
  );
};

__expose({
  scrollToBottom,
  showDraggable,
});

const getBreakpoint = (): number => {
  return window.innerHeight * props.initialBreakpoint;
};

if (props.draggable) {
  window.addEventListener("resize", getBreakpoint);
}

const scrollTop = ref<number>(0);
const isDraggableTouched = ref<boolean>(false);
const isOnMove = ref<boolean>(false);
const breakpoint = ref<number>(getBreakpoint());
const isDirectionTop = ref<boolean>(true);
const isOnAutoScroll = ref<boolean>(false);
const isFullscreen = ref<boolean>(scrollTop.value > breakpoint.value);
const isFullscreenView = ref<boolean>(scrollTop.value > breakpoint.value);
const scrollPercents = ref<number>(
  (breakpoint.value - scrollTop.value - props.offsetTop) / breakpoint.value
);
const safeArea = {
  top: parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--ion-safe-area-top"
    )
  ),
  bottom: parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--ion-safe-area-bottom"
    )
  ),
  // top: 50,
  // bottom: 50,
};

onMounted(() => {
  setTimeout(() => {
    safeArea.top = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--ion-safe-area-top"
      )
    );
    safeArea.bottom = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--ion-safe-area-bottom"
      )
    );
  }, 500);
});

const draggableOptions = {
  edges: breakpoint.value / 3,
  duration: 350,
};

const onContentScroll = (event: IonContentCustomEvent<ScrollDetail>) => {
  scrollPercents.value =
    (breakpoint.value - scrollTop.value) / breakpoint.value;
  isDirectionTop.value =
    scrollTop.value < event.detail.scrollTop + props.offsetTop;
  scrollTop.value = event.detail.scrollTop + props.offsetTop;
  isFullscreenView.value =
    scrollTop.value + 2 + safeArea.top >= breakpoint.value;
};

const onContentScrollEnd = () => {
  isOnMove.value = false;
  if (!isDraggableTouched.value && !isOnAutoScroll.value) {
    alignDraggableContent();
  }
};

const onContentScrollStart = () => {
  isOnMove.value = true;
};

const onContentTouchEnd = () => {
  isDraggableTouched.value = false;

  if (!isOnMove.value) {
    alignDraggableContent();
  }
};

const onContentTouchStart = () => {
  isDraggableTouched.value = true;
};

const alignDraggableContent = async () => {
  if (!isDirectionTop.value && scrollTop.value < breakpoint.value) {
    // down
    isOnAutoScroll.value = true;
    await content?.value?.$el.scrollToPoint(
      0,
      scrollTop.value - props.offsetTop + safeArea.top <
        breakpoint.value - draggableOptions.edges
        ? 0
        : breakpoint.value - props.offsetTop - safeArea.top,
      draggableOptions.duration
    );
    setTimeout(() => {
      isOnAutoScroll.value = false;
    }, 200);
  } else if (
    isDirectionTop.value &&
    scrollTop.value > props.offsetTop &&
    breakpoint.value >= scrollTop.value
  ) {
    // up
    isOnAutoScroll.value = true;
    await content?.value?.$el.scrollToPoint(
      0,
      scrollTop.value > draggableOptions.edges
        ? breakpoint.value - props.offsetTop - safeArea.top
        : 0,
      draggableOptions.duration
    );
    setTimeout(() => {
      isOnAutoScroll.value = false;
    }, 200);
  }

  isFullscreen.value =
    scrollTop.value + 1 >= breakpoint.value - props.offsetTop;
};

onUnmounted(() => {
  if (props.draggable) {
    window.removeEventListener("resize", getBreakpoint);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), {
    class: _normalizeClass(["base-layout", { 'has-fixed-header': _ctx.headerFixed }]),
    style: _normalizeStyle({
      '--breakpoint': _ctx.initialBreakpoint * 100 + 'vh',
      '--offset': _ctx.offsetTop + 'px',
    })
  }, {
    default: _withCtx(() => [
      (_unref(loadingUser))
        ? (_openBlock(), _createBlock(_unref(IonSpinner), {
            key: 0,
            name: "lines",
            class: "spinner"
          }))
        : _createCommentVNode("", true),
      (!_unref(loadingUser))
        ? (_openBlock(), _createBlock(_unref(IonHeader), {
            key: 1,
            class: _normalizeClass(["header ion-no-border", { 'header--fixed': _ctx.headerFixed, 'header-fullscreen': !_unref(Capacitor).isNativePlatform() && _ctx.draggable && isFullscreenView.value}])
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "header")
            ]),
            _: 3
          }, 8, ["class"]))
        : _createCommentVNode("", true),
      (!_unref(loadingUser))
        ? (_openBlock(), _createBlock(_unref(IonContent), {
            key: 2,
            ref_key: "content",
            ref: content,
            fullscreen: true,
            "scroll-events": _ctx.draggable,
            onIonScroll: onContentScroll,
            onTouchend: onContentTouchEnd,
            "scroll-y": _ctx.initialBreakpoint < 1,
            onTouchstart: onContentTouchStart,
            onIonScrollEnd: onContentScrollEnd,
            onIonScrollStart: onContentScrollStart,
            class: _normalizeClass(['page-content','has-footer', 'has-header', _ctx.$attrs.class, {
        'page-content--full-height': _ctx.contentFullHeight,
        'page-content--fullscreen': isFullscreenView.value,
        'top-24': _unref(isPlatform)('ios'),
      }])
          }, {
            default: _withCtx(() => [
              (_ctx.draggable)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(_unref(IonBackdrop), {
                      visible: true,
                      tappable: false,
                      style: _normalizeStyle({
            opacity:
              1 - scrollPercents.value < 0
                ? 0
                : 1 - scrollPercents.value > 1
                ? 1
                : 1 - scrollPercents.value,
          })
                    }, null, 8, ["style"]),
                    _createElementVNode("div", {
                      class: _normalizeClass(["fixed-content", { 'fixed-content--fullheight': _ctx.initialBreakpoint === 1 }])
                    }, [
                      _renderSlot(_ctx.$slots, "static")
                    ], 2),
                    _createElementVNode("div", {
                      class: "draggable-content",
                      style: _normalizeStyle({
            background: _ctx.draggableBackground,
            '--background': _ctx.draggableBackground,
          })
                    }, [
                      _createElementVNode("span", {
                        draggable: "true",
                        class: _normalizeClass(["draggable-content__drag-handle", {
              'draggable-content__drag-handle--fixed': isFullscreenView.value && _unref(Capacitor).isNativePlatform(),
            }]),
                        style: _normalizeStyle({ '--offset': _ctx.offsetTop + 'px' })
                      }, null, 6),
                      _renderSlot(_ctx.$slots, "draggable")
                    ], 4)
                  ], 64))
                : _renderSlot(_ctx.$slots, "content", { key: 1 })
            ]),
            _: 3
          }, 8, ["scroll-events", "scroll-y", "class"]))
        : _createCommentVNode("", true),
      (!_ctx.hideNavigationMenu && !_unref(loadingUser))
        ? (_openBlock(), _createBlock(_unref(IonFooter), {
            key: 3,
            class: _normalizeClass([{ 'page-footer--content-width': !_ctx.fullWidthFooter }, "page-footer ion-no-border"]),
            collapse: "fade",
            "no-border": ""
          }, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "footer", {}, () => [
                _createVNode(NavigationMenu, { items: _unref(menu) }, null, 8, ["items"])
              ])
            ]),
            _: 3
          }, 8, ["class"]))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["class", "style"]))
}
}

})